import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, CalendarDays, ArrowRight, Share2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { blogPosts } from './BlogPage';
export function BlogPostPage() {
  const {
    id
  } = useParams();
  const post = blogPosts.find(p => p.id === id);
  const otherPosts = blogPosts.filter(p => p.id !== id).slice(0, 3);
  if (!post) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-cream-50 pt-20" data-id="element-194">
        <h1 className="text-3xl font-display font-bold text-forest-900 mb-4" data-id="element-195">
          Article not found
        </h1>
        <Link to="/blog" data-id="element-196">
          <Button data-id="element-197">Back to Blog</Button>
        </Link>
      </div>;
  }
  return <div className="min-h-screen bg-cream-50 pt-20 pb-24" data-id="element-198">
      {/* Breadcrumb */}
      <div className="bg-white border-b-2 border-forest-100" data-id="element-199">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-id="element-200">
          <Link to="/blog" className="inline-flex items-center text-sm font-bold text-forest-700 hover:text-ember-600 transition-colors group" data-id="element-201">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-202" />{' '}
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-203">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-204">
          <Badge variant="outline" className="mb-6" data-id="element-205">
            {post.category}
          </Badge>
          <h1 className="text-3xl md:text-5xl font-display font-bold text-forest-900 leading-tight mb-6" data-id="element-206">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b-2 border-forest-50" data-id="element-207">
            <span className="flex items-center" data-id="element-208">
              <User className="w-4 h-4 mr-1.5" data-id="element-209" /> {post.author}
            </span>
            <span className="flex items-center" data-id="element-210">
              <CalendarDays className="w-4 h-4 mr-1.5" data-id="element-211" /> {post.date}
            </span>
            <span className="flex items-center" data-id="element-212">
              <Clock className="w-4 h-4 mr-1.5" data-id="element-213" /> {post.readTime}
            </span>
            <button className="ml-auto flex items-center text-forest-700 hover:text-ember-500 font-bold transition-colors" data-id="element-214">
              <Share2 className="w-4 h-4 mr-1.5" data-id="element-215" /> Share
            </button>
          </div>
        </motion.div>

        {/* Featured Image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="mb-12" data-id="element-216">
          <div className="rounded-3xl overflow-hidden border-2 border-forest-100 shadow-xl" data-id="element-217">
            <img src={post.image} alt={post.title} className="w-full h-[300px] md:h-[450px] object-cover" data-id="element-218" />
          </div>
        </motion.div>

        {/* Article Body */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="prose prose-lg max-w-none mb-16" data-id="element-219">
          <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-forest-50 shadow-lg" data-id="element-220">
            <p className="text-xl text-gray-700 leading-relaxed mb-6 font-medium" data-id="element-221">
              {post.excerpt}
            </p>

            <h2 className="text-2xl font-display font-bold text-forest-900 mt-10 mb-4" data-id="element-222">
              Current Regulatory Landscape
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-223">
              The Food Safety Authority of Ireland (FSAI) continues to
              strengthen enforcement across the food industry. In 2025, there
              has been a notable increase in inspection frequency and closure
              orders issued to non-compliant businesses. Staying informed about
              these regulatory changes is essential for every food business
              operator in Ireland.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-224">
              Whether you operate a restaurant, café, catering company, or
              retail food premises, the compliance requirements are clear:
              maintain a documented HACCP system, ensure all staff hold valid
              food safety certification, and keep accurate monitoring records at
              all times.
            </p>

            <h2 className="text-2xl font-display font-bold text-forest-900 mt-10 mb-4" data-id="element-225">
              What This Means for Irish Food Businesses
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-226">
              Recent FSAI enforcement reports highlight that inadequate staff
              training remains one of the most common reasons for compliance
              failures. Businesses that invest in up-to-date, certified training
              programmes are significantly less likely to face enforcement
              action.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-227">
              Online on-demand training has become the standard for Irish food
              businesses looking to maintain compliance efficiently. Staff can
              complete certified courses without operational disruption, and
              managers can track completion in real time through group
              dashboards.
            </p>

            <div className="bg-forest-50 rounded-2xl p-6 border border-forest-100 my-8" data-id="element-228">
              <p className="text-forest-800 font-bold text-lg mb-2" data-id="element-229">
                📋 Compliance Note
              </p>
              <p className="text-forest-700" data-id="element-230">
                All food handlers in Ireland must hold valid food safety
                certification aligned with FSAI guidelines. Certificates are
                typically valid for 3 years, after which refresher training is
                required.
              </p>
            </div>

            <h2 className="text-2xl font-display font-bold text-forest-900 mt-10 mb-4" data-id="element-231">
              Staying Compliant
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-232">
              Ensuring your team's certifications are current is a legal
              obligation under Irish food safety legislation. Our FSAI-compliant
              online courses cover HACCP Levels 1-3, Allergen Awareness, and
              Food Safety Management — all CPD-accredited with instant
              certification upon completion.
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-forest-900 to-forest-800 rounded-3xl p-10 text-center text-white mb-16 relative overflow-hidden" data-id="element-233">
          <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-234"></div>
          <div className="relative z-10" data-id="element-235">
            <h3 className="text-2xl font-display font-bold mb-4" data-id="element-236">
              Ready to get your team certified?
            </h3>
            <p className="text-forest-200 mb-6 max-w-xl mx-auto" data-id="element-237">
              Browse our range of FSAI-compliant online courses and get started
              today.
            </p>
            <Link to="/courses" data-id="element-238">
              <Button variant="secondary" size="lg" data-id="element-239">
                Explore Courses <ArrowRight className="w-4 h-4 ml-2" data-id="element-240" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {otherPosts.length > 0 && <div className="mb-16" data-id="element-241">
            <h3 className="text-2xl font-display font-bold text-forest-900 mb-8" data-id="element-242">
              More Articles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-243">
              {otherPosts.map(related => <Link to={`/blog/${related.id}`} key={related.id} className="group block" data-id="element-244">
                  <div className="bg-white rounded-2xl overflow-hidden border-2 border-forest-100 hover:border-forest-300 hover:shadow-lg transition-all" data-id="element-245">
                    <div className="h-36 overflow-hidden" data-id="element-246">
                      <img src={related.image} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-247" />
                    </div>
                    <div className="p-5" data-id="element-248">
                      <h4 className="font-display font-bold text-forest-900 group-hover:text-forest-700 transition-colors leading-tight line-clamp-2" data-id="element-249">
                        {related.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-2" data-id="element-250">
                        {related.date} · {related.readTime}
                      </p>
                    </div>
                  </div>
                </Link>)}
            </div>
          </div>}
      </article>
    </div>;
}